import React from 'react';

import type { StoryboardBeatInput, VideoAspectRatio } from '@/store/slices/creativeJobs';

/**
 * Actions the canvas nodes call to mutate the draft, exposed via context so the nodes need no prop
 * threading through React Flow. Provided by {@link StoryboardCanvas}; kept in its own module so the
 * node components can consume it without importing the canvas (which imports them, a cycle otherwise).
 */
export interface CanvasActions {
  updateBeat: (i: number, patch: Partial<StoryboardBeatInput>) => void;
  removeBeat: (i: number) => void;
  moveBeat: (i: number, dir: -1 | 1) => void;
  setAspect: (aspect: VideoAspectRatio) => void;
  setCta: (cta: string) => void;
  /** Regenerate the ad from a given beat (1-based): reuses beats 1..K-1 and re-runs K..N. Called by
   *  a node's Regenerate control; the composer branches a resume run off the current job. */
  regenerateFrom: (beatIndex: number) => void;
  /** Video renders are blocked (a mature primary art style): a node's Regenerate control is disabled and
   *  self-gates. The composer's regenerateFrom gates too. */
  renderBlocked: boolean;
  /** Open the beat-preview modal for a beat (1-based). */
  viewBeat: (index: number) => void;
  /** The durations (seconds) the chosen video model accepts — the beat duration dropdown's options. */
  videoDurations: number[];
  /** Select which finished-ad version plays in the output node (by its url). Called by the card's version
   *  picker; the composer owns the selected-version state. */
  selectVersion: (url: string) => void;
}

export const CanvasActionsContext = React.createContext<CanvasActions | null>(null);

/** Read the canvas actions; throws if a node is rendered outside the provider (a programmer error). */
export const useCanvasActions = (): CanvasActions => {
  const ctx = React.useContext(CanvasActionsContext);
  if (!ctx) throw new Error('CanvasActionsContext is missing a provider');
  return ctx;
};

/**
 * The on-canvas control card's content (promotion picker + prompt + regenerate), supplied by the composer
 * and rendered inside the `promo` node (see PromoNode). Passed through context rather than node data on
 * purpose: React Flow syncs node data into its store a render late, which would reset the prompt textarea's
 * caret mid-type; a context value propagates synchronously, so the live value reaches the input. Null when
 * there is no control card (mobile, where the controls stack outside the canvas).
 */
export const PromoPanelContext = React.createContext<React.ReactNode>(null);
export const usePromoPanel = (): React.ReactNode => React.useContext(PromoPanelContext);
